import { Chevron } from "@components/icons"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14BoldStyles,
  body14Styles,
  body16BoldStyles,
  body16MainStyles,
  h4Styles,
  h5Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

import Modal, { ModalBaseProps } from "../index"

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 72px;
  margin-bottom: 40px;
  border-bottom: 1px solid ${colors.dividerLineGray};
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    height: 56px;
    margin-bottom: 85px;
    border-bottom: none;
    padding: 0;
  }
`

const HeaderButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  outline: none;
  height: 32px;
  width: 32px;
  transform: rotate(180deg) translateY(1px);
  padding: 0;
  margin-right: 8px;

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const HeaderText = styled.div`
  ${smallcapsBold13Styles}

  @media ${breakpoints.laptop} {
    ${h4Styles}
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`

const Row = styled.div`
  flex-direction: row;
  flex-wrap: wrap;
  display: flex;
  width: 100%;
  padding: 24px 0;
  border-bottom: 1px solid ${colors.dividerLineGray};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }

  @media ${breakpoints.laptop} {
    padding: 48px 0;
  }
`

const TitleCol = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;

  @media (min-width: 1024px) {
    width: 60%;
  }
`

const SpecCol = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;

  @media (min-width: 1024px) {
    width: 40%;
    padding-left: calc(1 / 12 * 100vw);
  }
`

const SpecTitle = styled.div`
  ${h5Styles};
  padding: 16px 20px;

  @media ${breakpoints.laptop} {
    padding: 16px 0;
  }
`

const SpecItem = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 0 auto;
  align-items: center;
  min-height: 56px;
  border-bottom: 1px solid ${colors.dividerLineGray};
  padding: 0 20px;

  &:last-child {
    border-bottom: none;
  }

  @media ${breakpoints.laptop} {
    padding: 16px 0;
  }
`

const SpecItemKey = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  ${body14BoldStyles}
  width: 111.62px;
  margin-right: 20px;

  @media ${breakpoints.laptop} {
    ${body16BoldStyles}
    margin-right: 40px;
  }
`

const SpecItemValue = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${body14Styles}
  color: ${colors.mediumGray};

  @media ${breakpoints.laptop} {
    ${body16MainStyles}
    color: ${colors.mediumGray};
  }
`

interface ProductSpecsModalProps extends ModalBaseProps {
  specSheets: Array<{
    productSpecification: Array<{
      body: string
      title: string
    }>
    title: string
  }>
}

const renderSpecSheet = (
  specSheets: {
    productSpecification: Array<{ body: string; title: string }>
    title: string
  },
  i
) => (
  <Row key={specSheets.title || i}>
    <TitleCol>
      <SpecTitle>{specSheets.title || "Product"}</SpecTitle>
    </TitleCol>
    <SpecCol>
      {specSheets.productSpecification.map(({ body, title }) => (
        <SpecItem key={title}>
          <SpecItemKey>{title}</SpecItemKey>
          <SpecItemValue>{body}</SpecItemValue>
        </SpecItem>
      ))}
    </SpecCol>
  </Row>
)

const ProductSpecsModal = ({ specSheets, ...rest }: ProductSpecsModalProps) => {
  return (
    <Modal {...rest}>
      <Container>
        <MobileAndTabletOnly>
          <Header onClick={rest.onRequestClose}>
            <HeaderButton>
              <Chevron strokeColor={colors.black} />
            </HeaderButton>
            <HeaderText>Specifications</HeaderText>
          </Header>
        </MobileAndTabletOnly>
        <DesktopOnly>
          <Header>
            <HeaderText>Specifications</HeaderText>
          </Header>
        </DesktopOnly>
        <Content>{specSheets?.map(renderSpecSheet)}</Content>
      </Container>
    </Modal>
  )
}

export default ProductSpecsModal
